import classnames from 'classnames';
import { parse, print } from 'graphql';
import _ from 'lodash';
import React, { CSSProperties, useEffect, useMemo, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import { PrismAsyncLight as Prism } from 'react-syntax-highlighter';
import { tomorrow } from 'react-syntax-highlighter/dist/esm/styles/prism';

import { prismStudioThemeLight } from './prismStudioThemeLight';

export const GraphqlCodeBlock = ({
  className,
  sdl,
  darkMode,
}: {
  className?: string;
  sdl: string;
  darkMode?: boolean;
}) => {
  const chunkedSchemaDefinitions = useMemo(
    () => _.chunk(parse(sdl).definitions, 30),
    [sdl],
  );
  return (
    <div className={className}>
      {chunkedSchemaDefinitions.map((definitions) => {
        const chunkSdl = definitions.map(print).join('\n');
        return (
          <SingleGraphqlCodeBlock
            key={chunkSdl}
            sdl={chunkSdl}
            darkMode={darkMode}
          />
        );
      })}
    </div>
  );
};

export const SingleGraphqlCodeBlock = ({
  sdl,
  darkMode = false,
  className,
  preWrapWhiteSpace = true,
  style: styleOverrides,
}: {
  sdl: string;
  darkMode?: boolean;
  className?: string;
  preWrapWhiteSpace?: boolean;
  style?: React.CSSProperties;
}) => {
  const [inViewRef, inView] = useInView({ triggerOnce: true });
  const [shouldHighlight, setShouldHighlight] = useState(inView);
  useEffect(() => {
    if (inView) {
      setShouldHighlight(true);
    }
  }, [inView]);

  if (!shouldHighlight) {
    return (
      // match dom structure from react-syntax-highlighter
      <pre ref={inViewRef} className={classnames('leading-normal', className)}>
        <code className="whitespace-pre-wrap">{sdl}</code>
      </pre>
    );
  }
  return (
    <Prism
      language="graphql"
      style={darkMode ? tomorrow : prismStudioThemeLight}
      PreTag={({
        style,
        ...props
      }: {
        className?: string;
        style: CSSProperties;
      }) => (
        <pre
          {...props}
          className={classnames(props.className, className)}
          style={{
            ..._.omit(
              style,
              'fontSize',
              'fontFamily',
              'padding',
              'margin',
              'background',
            ),
            ...styleOverrides,
          }}
        />
      )}
      CodeTag={({ style, ...props }: { style: CSSProperties }) => (
        <code
          {...props}
          style={{
            ..._.omit(style, 'fontFamily'),
            whiteSpace: preWrapWhiteSpace ? 'pre-wrap' : 'pre',
            ...styleOverrides,
          }}
        />
      )}
    >
      {sdl}
    </Prism>
  );
};
